<template>
  <ul class="list-of-stc-member-products">
    <LazyLoadWhenInViewport
      v-for="(memberId, index) in visibleList"
      :key="memberId"
      tag="li"
      :disable-observer="!hasListChanged && !isMounted && index < INITIAL_VISIBLE_ITEM_COUNT"
      :with-transition="hasListChanged && isMounted && index > 0"
      class="member-product-item"
    >
      <slot name="item" :item-id="memberId"></slot>
      <template #fallback>
        <CompactProductSkeleton v-if="isMobile || isTablet" />
        <Skeleton v-else />
      </template>
    </LazyLoadWhenInViewport>
  </ul>

  <!-- Teleport the button to a container that spans the full width of the page -->
  <Teleport defer to="#stc-footer-actions">
    <button v-if="shouldShowMoreButton" class="show-more-stc-members secondary button" @click="onLoadMore">
      Show {{ moreItemsCount }} More Items
    </button>
  </Teleport>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useMounted } from '@vueuse/core';

import LazyLoadWhenInViewport from '~/components/common/NewLazyLoadWhenInViewport.vue';
import Skeleton from './Skeleton.vue';
import CompactProductSkeleton from './CompactProductSkeleton.vue';

import { useDeviceType } from '~/composables/utils/useDeviceType';

const INITIAL_VISIBLE_ITEM_COUNT = 5;
const isMounted = useMounted();

const props = defineProps<{
  list: number[];
}>();

const { isMobile, isTablet } = useDeviceType();

const visibleItemCount = ref(INITIAL_VISIBLE_ITEM_COUNT);
const hasListChanged = ref<boolean>(false);

const visibleList = computed(() => props.list.slice(0, visibleItemCount.value));
const shouldShowMoreButton = computed(() => visibleItemCount.value < props.list.length);
const moreItemsCount = computed<number>(() => props.list.length - visibleItemCount.value);

const onLoadMore = () => {
  visibleItemCount.value = props.list.length;
};

watch(
  () => props.list,
  (_, prevList) => {
    if (prevList && !hasListChanged.value) {
      hasListChanged.value = true;
    }
    // Reset expansion state when changing tabs
    visibleItemCount.value = INITIAL_VISIBLE_ITEM_COUNT;
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.list-of-stc-member-products {
  list-style: none;
  margin: 0;
  padding: 0;

  &:deep(.member-product-item) {
    height: 100%;
    border-bottom: 1px solid $gray-5-color;

    @include breakpoint(large up) {
      border-bottom: 0;
      @include padding-z();

      &:not(:first-child) {
        @include margin-top-xxxl();
      }
    }
  }
}

// Styles for the teleported button
// Note: Since it's teleported outside of this component, these styles
// will only apply if the target container doesn't have CSS scoping issues.
// We use a global-like selector or ensure the parent handles centering.
.show-more-stc-members {
  display: block;
  margin: 24px auto 0;
  width: 100%;
  max-width: 335px;

  @include breakpoint(large up) {
    margin-top: 72px;
  }
}
</style>
